// =============================================================================
// <Odometre> — le chiffre roule (moment M5, lot L5). Moteur : odometreMoteur.js.
//
//   <Odometre valeur={10} />                          « 10 »
//   <Odometre valeur={34} decimales={1} suffixe=" M€" />
//   <Odometre valeur={n} formater={(v) => `${v} sur 13`} regime="ceremonie" />
//
// Remplace CountUpNumber (qui recomptait depuis 0 à chaque montage et à chaque
// onSnapshot) ; même esprit que NombreFluide du Vote AG, étendu à toute l'app :
// depuis la valeur précédente, sens selon la variation, aucune valeur
// intermédiaire fausse, lecteur d'écran sur la valeur finale.
// Composant de CLASSE pour getSnapshotBeforeUpdate : c'est le seul moment où
// l'on peut lire où les chiffres SONT affichés avant que React n'écrive la
// nouvelle valeur (FLIP exact, même quand le nombre change de largeur).
// Au premier affichage : le texte, sans animation. Sous jsdom (tests), en
// mouvement réduit (fondu de 160 ms), onglet caché : pas de roulement.
// =============================================================================
import { Component } from "react";
import { nombreFr, INSECABLE } from "./nombresFr";
import { MOUVEMENT, animer, peutAnimer, mouvementReduit } from "./mouvement";

// Le moteur (mesure, bandes, FLIP) est chargé À LA DEMANDE, hors du bundle
// principal : préchargé au repos dès qu'un odomètre est affiché ; tant qu'il
// n'est pas là, la valeur change sans rouler (jamais d'état faux).
let moteur = null;
let chargement = null;
export function chargerMoteurOdometre() {
  if (!chargement) {
    chargement = import("./odometreMoteur")
      .then((m) => { moteur = m; return m; })
      .catch(() => { chargement = null; return null; });
  }
  return chargement;
}

/** Texte affiché pour des props données (formateur fr-FR par défaut). */
export function texteOdometre({ valeur, formater, decimales = 0, prefixe = "", suffixe = "" }) {
  const v = Number(valeur);
  const coeur = typeof formater === "function"
    ? formater(Number.isFinite(v) ? v : 0)
    : nombreFr(Number.isFinite(v) ? v : 0, { decimales, fixe: decimales > 0 });
  return `${prefixe}${coeur}${suffixe}`;
}

export class Odometre extends Component {
  constructor(props) {
    super(props);
    this.racine = null;
    this.annuler = null;
    this.poserRacine = (el) => { this.racine = el; };
  }

  peutRouler() {
    const r = this.racine;
    if (!r || !peutAnimer(r)) return false;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return false;
    return true;
  }

  componentDidMount() {
    if (!this.peutRouler() || moteur) return;
    const auRepos = typeof window !== "undefined" && window.requestIdleCallback
      ? (f) => window.requestIdleCallback(f, { timeout: 2000 })
      : (f) => setTimeout(f, 300);
    auRepos(() => { chargerMoteurOdometre(); });
  }

  getSnapshotBeforeUpdate(prevProps) {
    const avant = texteOdometre(prevProps);
    const apres = texteOdometre(this.props);
    if (avant === apres || !this.peutRouler()) return null;
    if (mouvementReduit()) return { fondu: true };
    if (!moteur) { chargerMoteurOdometre(); return null; }
    return { avant, instantane: moteur.lireInstantane(this.racine, avant), sens: Number(this.props.valeur) >= Number(prevProps.valeur) ? 1 : -1 };
  }

  componentDidUpdate(prevProps, prevState, snap) {
    if (!snap || !this.racine) return;
    if (snap.fondu) {
      const t = this.racine.querySelector(".odo-t");
      animer(t, [{ opacity: 0 }, { opacity: 1 }], { duration: MOUVEMENT.durees.rapide, easing: MOUVEMENT.courbes.sortie });
      return;
    }
    if (this.annuler) this.annuler();
    this.annuler = moteur.rouler(this.racine, snap.instantane, texteOdometre(this.props), {
      sens: snap.sens, regime: this.props.regime || "quotidien",
    });
  }

  componentWillUnmount() {
    if (this.annuler) this.annuler();
  }

  render() {
    const { className = "", style, titre } = this.props;
    const texte = texteOdometre(this.props);
    return (
      <span ref={this.poserRacine} className={`odo${className ? ` ${className}` : ""}`} style={style} title={titre}>
        <span className="odo-t">{texte}</span>
        <span className="odo-v" aria-hidden="true" />
      </span>
    );
  }
}

// Formats historiques de CountUpNumber, à la française (nombresFr.js).
const FORMATS_COMPTEUR = {
  number: (v) => nombreFr(Math.round(v)),
  pct: (v) => nombreFr(Math.round(v)) + INSECABLE + "%",
  decimal: (v) => nombreFr(v, { decimales: 1, fixe: true }),
};

/** <CountUpNumber> garde son nom et ses props (valeur, format, préfixe,
 *  suffixe, classe) ; `duration` est ignorée : le chiffre roule depuis la
 *  valeur précédente au lieu de recompter depuis 0. */
export function CountUpNumber({ value, format = "number", prefix = "", suffix = "", className = "" }) {
  return (
    <Odometre valeur={Number(value) || 0} formater={FORMATS_COMPTEUR[format] || FORMATS_COMPTEUR.number}
      prefixe={prefix} suffixe={suffix} className={className} />
  );
}
